import { useQuery } from '@apollo/client'
import { fetchBalance } from '@wagmi/core'
import { formatEther, parseEther } from 'ethers/lib/utils'
import { useEffect, useMemo, useState } from 'react'
import { useSigner } from 'wagmi'

import { WITHDRAW_MODE } from '@/constants'
import {
  FeesMevPoolsBalanceQuery,
  FeesMevWithdrawBalanceQuery,
  ProtectedPoolsBalanceQuery,
  ProtectedWithdrawBalanceQuery,
  RageQuitAssistantQuery,
  UnstakedValidatorsQuery
} from '@/graphql/queries/WithdrawBalanceQuery'
import { calcBalance, calcPoolBalance } from '@/utils/calcBalance'

import client from '../graphql/client'
import { GiantFeesAndMevPoolsQuery } from '../graphql/queries/GiantFeesAndMevPoolsQuery'
import { GiantSavETHPoolQuery } from '../graphql/queries/GiantSavETHPoolQuery'
import { useCustomAccount, useLsdValidators, useSDK } from '.'

export type ILSDNetworkOption = Record<
  string,
  {
    ticker: string
    balance: number
    savETHPool?: string
    feesAndMevPool?: string
    blsKeys?: string[]
    rawBalance?: string
    ethBalance?: string
    dethBalance?: string
  }
>

export type IBlsKeyOption = {
  blsKey: string
  balance: number
  lpToken: string
  rawBalance: string
  ethBalance?: string
  dethBalance?: string
}

export const useWithdrawBalance = (mode?: WITHDRAW_MODE) => {
  const { sdk } = useSDK()
  const { data: signer } = useSigner()
  const { account } = useCustomAccount()
  const address = account?.address

  // const account = { address: '0xad263487ae1a7acc9494c17116c4c9c93552c71f' }
  // const account = { address: '0x7d8381afbada9ab3ec16de3f17ad0e3a2af58b79' }

  const [balance, setBalance] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(true)
  const [isRefetch, setIsRefetch] = useState<boolean>(false)
  const [isEligible, setEligible] = useState(true)
  const [lastInteractedTimestamp, setLastInteractedTimestamp] = useState<number>(0)
  const [rageQuitValidators, setRageQuitValidators] = useState<any>()
  const [rageQuitLSDs, setRageQuitLSDs] = useState<any>()

  const { data: giantSavETHData } = useQuery(GiantSavETHPoolQuery)
  const { data: giantFeesAndMEVData } = useQuery(GiantFeesAndMevPoolsQuery)

  const { count, loading: validatorLoading } = useLsdValidators(address ?? '')

  const { data: { protectedBatches } = {} } = useQuery(ProtectedWithdrawBalanceQuery, {
    variables: { account: address?.toLowerCase() },
    skip: !address,
    fetchPolicy: 'network-only'
  })

  const { data: { lptokens: protectedLPs } = {} } = useQuery(ProtectedPoolsBalanceQuery, {
    variables: { account: address?.toLowerCase() },
    skip: !address,
    fetchPolicy: 'network-only'
  })

  const { data: { feesAndMevBatches } = {} } = useQuery(FeesMevWithdrawBalanceQuery, {
    variables: { account: address?.toLowerCase() },
    skip: !address,
    fetchPolicy: 'network-only'
  })

  const { data: { lptokens: feesAndMevLPs } = {} } = useQuery(FeesMevPoolsBalanceQuery, {
    variables: { account: address?.toLowerCase() },
    skip: !address,
    fetchPolicy: 'network-only'
  })

  const {
    data: {
      lptokens,
      nodeRunners,
      protectedBatches: unstakedProtectedBatches,
      feesAndMevBatches: unstakedFeesAndMevBatches
    } = {}
  } = useQuery(UnstakedValidatorsQuery, {
    variables: { account: address?.toLowerCase() },
    skip: !address,
    fetchPolicy: 'network-only'
  })

  useEffect(() => {
    const getRageQuit = async () => {
      let nodeValidators = [],
        lpValidatorsAssistantKeys: any = [],
        lpValidators: any = [],
        unstakedGiantBalance: any = []

      if (unstakedProtectedBatches && unstakedProtectedBatches.length > 0) {
        unstakedProtectedBatches.map((item: any) => {
          const blsKey = item.lsdValidator.id
          // if (blsKey in unstakedGiantBalance)
          //   unstakedGiantBalance[blsKey] =
          //     unstakedGiantBalance[blsKey] +
          //     Number(formatEther(item.liquidityProviders[0].amount)) -
          //     Number(formatEther(item.liquidityProviders[0].withdrawn))
          // else {
          unstakedGiantBalance = [
            ...unstakedGiantBalance,
            {
              giantLPAmount:
                Number(formatEther(item.liquidityProviders[0].amount)) -
                Number(formatEther(item.liquidityProviders[0].withdrawn)),
              giantSavLPAmount:
                Number(formatEther(item.liquidityProviders[0].amount)) -
                Number(formatEther(item.liquidityProviders[0].withdrawn)),
              blsKey,
              totalETHFunded: item.lsdValidator.totalETHFromGiantProtectedStakingPool,
              protectedlpTokenId: item.vaultLPToken.id,
              savETHPool: item.lsdValidator.smartWallet.liquidStakingNetwork.savETHPool,
              lsdId: item.lsdValidator.smartWallet.liquidStakingNetwork.id,
              ticker: item.lsdValidator.smartWallet.liquidStakingNetwork.ticker
            }
          ]
        })
      }

      if (unstakedFeesAndMevBatches && unstakedFeesAndMevBatches.length > 0) {
        unstakedFeesAndMevBatches.map((item: any) => {
          const blsKey = item.lsdValidator.id
          // if (item.blsPublicKey in unstakedGiantBalance)
          //   unstakedGiantBalance[item.blsPublicKey] =
          //     unstakedGiantBalance[item.blsPublicKey] +
          //     Number(formatEther(item.liquidityProviders[0].amount)) -
          //     Number(formatEther(item.liquidityProviders[0].withdrawn))
          // else
          //   unstakedGiantBalance[item.blsPublicKey] =
          //     Number(formatEther(item.liquidityProviders[0].amount)) -
          //     Number(formatEther(item.liquidityProviders[0].withdrawn))

          unstakedGiantBalance = [
            ...unstakedGiantBalance,
            {
              giantLPAmount:
                Number(formatEther(item.liquidityProviders[0].amount)) -
                Number(formatEther(item.liquidityProviders[0].withdrawn)),
              giantFeesLPAmount:
                Number(formatEther(item.liquidityProviders[0].amount)) -
                Number(formatEther(item.liquidityProviders[0].withdrawn)),
              blsKey,
              totalETHFunded: item.lsdValidator.totalETHFromGiantFeesAndMevPool,
              feeslpTokenId: item.vaultLPToken.id,
              feesAndMevPool: item.lsdValidator.smartWallet.liquidStakingNetwork.feesAndMevPool,
              lsdId: item.lsdValidator.smartWallet.liquidStakingNetwork.id,
              ticker: item.lsdValidator.smartWallet.liquidStakingNetwork.ticker
            }
          ]
        })
      }

      if (lptokens && lptokens.length > 0) {
        lpValidatorsAssistantKeys = await Promise.all(
          lptokens.map(async (item: any) => {
            const assistKey = await sdk?.wizard.getRageQuitAssistantForBLSPublicKey(
              item.liquidStakingNetwork.feesAndMevPool,
              item.blsPublicKey
            )

            return {
              assistKey,
              lpTokenId: item.id,
              tokenType: item.tokenType,
              lpAmount:
                Number(formatEther(item.liquidityProviders[0].amount)) -
                Number(formatEther(item.liquidityProviders[0].withdrawn)),
              blsKey: item.blsPublicKey,
              lsdId: item.liquidStakingNetwork.id,
              ticker: item.liquidStakingNetwork.ticker,
              issuer: item.issuer
            }
          })
        )
      }

      if (nodeRunners && nodeRunners.length > 0) {
        nodeValidators = await Promise.all(
          nodeRunners[0].validators.map(async (item: any) => {
            const assistant = await sdk?.wizard.getRageQuitAssistantForBLSPublicKey(
              item.smartWallet.liquidStakingNetwork.feesAndMevPool,
              item.id
            )
            // const amount = await sdk?.wizard.rageQuitNodeOperatorClaimPreview(assistant)
            let amount = 0
            try {
              const contract = (await sdk?.contractInstance).rageQuitAssistant(assistant)
              amount = await contract.nodeOperatorClaimPreview(address)
            } catch (error) {
              console.log(error)
            }

            return {
              blsKey: item.id,
              assistKey: assistant,
              amount: amount ? Number(formatEther(amount)) : 0,
              nodeOperatorBalance: amount ? Number(formatEther(amount)) : 0,
              lsdId: item.smartWallet.liquidStakingNetwork.id,
              ticker: item.smartWallet.liquidStakingNetwork.ticker,
              isNodeOperator: true
            }
          })
        )
      }

      if (lpValidatorsAssistantKeys && lpValidatorsAssistantKeys.length > 0) {
        lpValidators = await Promise.all(
          lpValidatorsAssistantKeys.map(
            async ({
              assistKey,
              blsKey,
              lsdId,
              ticker,
              lpTokenId,
              lpAmount,
              tokenType,
              issuer
            }: any) => {
              const { data: { lsdrageQuitAssistants: lsdrageQuitAssistants } = [] } =
                await client.query({
                  query: RageQuitAssistantQuery,
                  variables: {
                    account: address?.toLowerCase(),
                    rageQuitAssistant: assistKey.toLowerCase()
                  }
                })

              let dETHAmount = 0,
                sETHAmount = 0

              if (lsdrageQuitAssistants.length > 0) {
                const lsdRageQuitAssistant = lsdrageQuitAssistants[0]

                if (
                  lsdRageQuitAssistant.dETHLiquidityProviders &&
                  lsdRageQuitAssistant.dETHLiquidityProviders.length > 0
                ) {
                  await Promise.all(
                    lsdRageQuitAssistant.dETHLiquidityProviders.map(async (item: any) => {
                      const balance = await fetchBalance({
                        address: address ?? '0x0',
                        token: lsdRageQuitAssistant.dETHLPToken.id
                      })

                      let amount = 0
                      if (balance.value.gt(0)) {
                        try {
                          amount = await sdk?.wizard.rageQuitDETHClaimPreview(
                            assistKey,
                            lsdRageQuitAssistant.dETHLPToken.id,
                            item.claimableAmount
                          )
                        } catch (error) {
                          console.log('Getting DETHClaim Preview Error')
                        }
                      }

                      dETHAmount += Number(amount ? formatEther(amount) : 0)
                    })
                  )
                }

                if (
                  lsdRageQuitAssistant.sETHLiquidityProviders &&
                  lsdRageQuitAssistant.sETHLiquidityProviders.length > 0
                ) {
                  await Promise.all(
                    lsdRageQuitAssistant.sETHLiquidityProviders.map(async (item: any) => {
                      const balance = await fetchBalance({
                        address: address ?? '0x0',
                        token: lsdRageQuitAssistant.sETHLPToken.id
                      })

                      let amount = 0
                      if (balance.value.gt(0)) {
                        try {
                          amount = await sdk?.wizard.rageQuitSETHClaimPreview(
                            assistKey,
                            lsdRageQuitAssistant.sETHLPToken.id,
                            item.claimableAmount
                          )
                        } catch (error) {
                          console.log('Getting SETHClaim Preview Error')
                        }
                      }

                      sETHAmount += Number(amount ?? 0)
                    })
                  )
                }
              }

              return {
                assistKey,
                tokenType,
                dETHAmount,
                sETHAmount,
                issuer,
                lpTokenId,
                lpAmount,
                amount: dETHAmount + sETHAmount + lpAmount,
                blsKey,
                lsdId,
                ticker,
                isNodeOperator: false
              }
            }
          )
        )
      }
      const rageQuitValidators: Record<string, any> = {}
      const lsdNetworks: any = {}

      nodeValidators.map((item: any) => {
        rageQuitValidators[item.blsKey] = {
          blsKey: item.blsKey,
          assistKey: item.assistKey,
          balance: item.amount,
          lsdId: item.lsdId,
          ticker: item.ticker,
          nodeOperatorBalance: item.nodeOperatorBalance,
          isNodeOperator: item.isNodeOperator
        }
        if (item.lsdId in lsdNetworks)
          lsdNetworks[item.lsdId] = {
            ticker: item.ticker,
            balance: item.amount + lsdNetworks[item.lsdId].balance
          }
        else
          lsdNetworks[item.lsdId] = {
            ticker: item.ticker,
            balance: item.amount
          }
      })

      lpValidators.map((item: any) => {
        if (item.blsKey in rageQuitValidators) {
          rageQuitValidators[item.blsKey] = {
            ...rageQuitValidators[item.blsKey],
            [item.tokenType]: item,
            balance: item.amount + rageQuitValidators[item.blsKey].balance
          }
        } else {
          rageQuitValidators[item.blsKey] = {
            [item.tokenType]: item,
            ticker: item.ticker,
            blsKey: item.blsKey,
            lsdId: item.lsdId,
            balance: item.amount
          }
        }

        if (item.lsdId in lsdNetworks)
          lsdNetworks[item.lsdId] = {
            ticker: item.ticker,
            balance: item.amount + lsdNetworks[item.lsdId].balance
          }
        else
          lsdNetworks[item.lsdId] = {
            ticker: item.ticker,
            balance: item.amount
          }
      })

      unstakedGiantBalance.map((item: any) => {
        if (item.blsKey in rageQuitValidators)
          rageQuitValidators[item.blsKey] = {
            ...rageQuitValidators[item.blsKey],
            ...item,
            balance: item.giantLPAmount + rageQuitValidators[item.blsKey].balance,
            giantLPAmount: item.giantLPAmount + rageQuitValidators[item.blsKey].giantLPAmount
          }
        else {
          rageQuitValidators[item.blsKey] = { ...item, balance: item.giantLPAmount }
        }

        if (item.lsdId in lsdNetworks)
          lsdNetworks[item.lsdId] = {
            ticker: item.ticker,
            balance: item.giantLPAmount + lsdNetworks[item.lsdId].balance
          }
        else
          lsdNetworks[item.lsdId] = {
            ticker: item.ticker,
            balance: item.giantLPAmount
          }
      })

      const rqValidators: Record<string, any> = {}
      Object.keys(rageQuitValidators).map((key: string) => {
        if (Number(rageQuitValidators[key].balance) == 0) {
          return
        }
        if (rageQuitValidators[key].lsdId in rqValidators)
          rqValidators[rageQuitValidators[key].lsdId] = [
            ...rqValidators[rageQuitValidators[key].lsdId],
            rageQuitValidators[key]
          ]
        else rqValidators[rageQuitValidators[key].lsdId] = [rageQuitValidators[key]]
      })

      const filteredNetworkEntries = Object.entries(lsdNetworks).filter(
        ([, v]: any) => Number(v.balance) > 0
      )
      const filteredNetworks = Object.fromEntries(filteredNetworkEntries)

      setRageQuitValidators(rqValidators)
      setRageQuitLSDs(filteredNetworks)
    }

    getRageQuit()
  }, [lptokens, nodeRunners, sdk])

  const protectedBlsKeys = useMemo(() => {
    const blsKeys: Record<string, IBlsKeyOption[]> = {}
    if (protectedLPs && protectedLPs.length > 0) {
      protectedLPs.map((item: any) => {
        if (item.liquidStakingNetwork.id in blsKeys) {
          blsKeys[item.liquidStakingNetwork.id] = [
            ...blsKeys[item.liquidStakingNetwork.id],
            {
              blsKey: item.blsPublicKey,
              balance: Number(formatEther(item.liquidityProviders[0].amount)),
              rawBalance: formatEther(item.liquidityProviders[0].amount),
              lpToken: item.id
            }
          ]
        } else
          blsKeys[item.liquidStakingNetwork.id] = [
            {
              blsKey: item.blsPublicKey,
              balance: Number(formatEther(item.liquidityProviders[0].amount)),
              rawBalance: formatEther(item.liquidityProviders[0].amount),
              lpToken: item.id
            }
          ]
      })
    }

    return blsKeys
  }, [protectedLPs])

  const feesAndMevBlsKeys = useMemo(() => {
    const blsKeys: Record<string, IBlsKeyOption[]> = {}
    if (feesAndMevLPs && feesAndMevLPs.length > 0) {
      feesAndMevLPs.map((item: any) => {
        if (item.liquidStakingNetwork.id in blsKeys) {
          blsKeys[item.liquidStakingNetwork.id] = [
            ...blsKeys[item.liquidStakingNetwork.id],
            {
              blsKey: item.blsPublicKey,
              balance: Number(formatEther(item.liquidityProviders[0].amount)),
              rawBalance: formatEther(item.liquidityProviders[0].amount),
              lpToken: item.id
            }
          ]
        } else
          blsKeys[item.liquidStakingNetwork.id] = [
            {
              blsKey: item.blsPublicKey,
              balance: Number(formatEther(item.liquidityProviders[0].amount)),
              rawBalance: formatEther(item.liquidityProviders[0].amount),
              lpToken: item.id
            }
          ]
      })
    }

    return blsKeys
  }, [feesAndMevLPs])

  const protectedLSDs = useMemo(() => {
    let giantBalance = 0
    if (protectedBatches) {
      const batches = protectedBatches.filter((item: any) => item.vaultLPToken === null)
      giantBalance = calcBalance(batches)
    }

    const networks: ILSDNetworkOption = {
      giant_pool: { ticker: 'Giant Pool', balance: giantBalance }
    }

    if (protectedLPs && protectedLPs.length > 0) {
      protectedLPs.map((item: any) => {
        if (item.liquidStakingNetwork.id in networks) {
          networks[item.liquidStakingNetwork.id].balance += Number(
            formatEther(item.liquidityProviders[0].amount)
          )
        } else
          networks[item.liquidStakingNetwork.id] = {
            ticker: item.liquidStakingNetwork.ticker,
            savETHPool: item.liquidStakingNetwork.savETHPool,
            feesAndMevPool: item.liquidStakingNetwork.feesAndMevPool,
            balance: Number(formatEther(item.liquidityProviders[0].amount))
          }
      })
    }

    return networks
  }, [protectedLPs, protectedBatches])

  const feesAndMevLSDs = useMemo(() => {
    let giantBalance = 0
    if (feesAndMevBatches) {
      const batches = feesAndMevBatches.filter((item: any) => item.vaultLPToken === null)
      giantBalance = calcBalance(batches)
    }

    const networks: ILSDNetworkOption = {
      giant_pool: { ticker: 'Giant Pool', balance: giantBalance }
    }
    if (feesAndMevLPs && feesAndMevLPs.length > 0) {
      feesAndMevLPs.map((item: any) => {
        if (item.liquidStakingNetwork.id in networks) {
          networks[item.liquidStakingNetwork.id].balance += Number(
            formatEther(item.liquidityProviders[0].amount)
          )
        } else
          networks[item.liquidStakingNetwork.id] = {
            ticker: item.liquidStakingNetwork.ticker,
            savETHPool: item.liquidStakingNetwork.savETHPool,
            feesAndMevPool: item.liquidStakingNetwork.feesAndMevPool,
            balance: Number(formatEther(item.liquidityProviders[0].amount))
          }
      })
    }

    return networks
  }, [feesAndMevLPs, feesAndMevBatches])

  const checkGiantLPTokenIfEligible = async (mode: WITHDRAW_MODE) => {
    if (signer) {
      const giantLPToken =
        mode === WITHDRAW_MODE.STAKING
          ? giantSavETHData.giantSavETHPools[0]?.giantLPToken
          : giantFeesAndMEVData.giantFeesAndMevPools[0]?.giantLPToken
      const timestamp = await sdk?.wizard.getLastInteractedTimestamp(address, giantLPToken)

      setLastInteractedTimestamp(timestamp)
      const isEligible = await sdk?.wizard.isEligibleToInteractWithGiantLPToken(timestamp)

      return isEligible
    }
  }

  const checkLPTokenIfEligible = async (lpToken: string) => {
    if (signer) {
      const timestamp = await sdk?.wizard.getLastInteractedTimestampForLPToken(address, lpToken)

      setLastInteractedTimestamp(timestamp)
      const isEligible = await sdk?.wizard.isEligibleToInteractWithLPToken(timestamp)

      return isEligible
    }

    return true
  }

  useEffect(() => {
    const initBalance = async () => {
      setLoading(true)
      let stakingBalance,
        feesMevBalance,
        nodeOperatorBalance,
        unstakedBalance = 0,
        totalBalance,
        batches

      switch (mode) {
        case WITHDRAW_MODE.STAKING:
          if (protectedBatches) {
            batches = protectedBatches.filter((item: any) => item.vaultLPToken === null)

            let lpBalance = 0
            if (protectedLPs && protectedLPs.length > 0) {
              lpBalance = calcPoolBalance(protectedLPs)
            }

            setBalance(Number(calcBalance(batches) + lpBalance).toFixed(2))
          } else setBalance('0')
          break

        case WITHDRAW_MODE.FEES_MEV:
          if (feesAndMevBatches) {
            batches = feesAndMevBatches.filter((item: any) => item.vaultLPToken === null)

            let lpBalance = 0
            if (feesAndMevLPs && feesAndMevLPs.length > 0) {
              lpBalance = calcPoolBalance(feesAndMevLPs)
            }

            setBalance(Number(calcBalance(batches) + lpBalance).toFixed(2))
          } else setBalance('0')

          break

        case WITHDRAW_MODE.NODE_OPERATOR:
          nodeOperatorBalance = parseEther(Number(count * 4).toFixed(2))
          setBalance(formatEther(nodeOperatorBalance))
          break

        case WITHDRAW_MODE.UNSTAKED_VALIDATOR:
          Object.keys(rageQuitLSDs).map((id) => (unstakedBalance += rageQuitLSDs[id].balance))

          setBalance(
            Number(unstakedBalance).toLocaleString(undefined, { maximumFractionDigits: 3 })
          )
          break

        default:
          if (protectedBatches) {
            batches = protectedBatches.filter((item: any) => item.vaultLPToken === null)

            let lpBalance = 0
            if (protectedLPs && protectedLPs.length > 0) {
              lpBalance = calcPoolBalance(protectedLPs)
            }

            stakingBalance = calcBalance(batches) + lpBalance
          } else stakingBalance = 0

          if (feesAndMevBatches) {
            batches = feesAndMevBatches.filter((item: any) => item.vaultLPToken === null)

            let lpBalance = 0
            if (feesAndMevLPs && feesAndMevLPs.length > 0) {
              lpBalance = calcPoolBalance(feesAndMevLPs)
            }

            feesMevBalance = calcBalance(batches) + lpBalance
          } else feesMevBalance = 0

          nodeOperatorBalance = Number(count * 4)
          totalBalance = stakingBalance + feesMevBalance + nodeOperatorBalance

          setBalance(Number(totalBalance).toFixed(2))
          break
      }

      setLoading(false)
    }

    if (
      sdk &&
      address &&
      !validatorLoading &&
      protectedBatches &&
      feesAndMevBatches &&
      rageQuitLSDs
    )
      initBalance()
  }, [
    sdk,
    validatorLoading,
    isRefetch,
    protectedBatches,
    feesAndMevBatches,
    protectedLPs,
    feesAndMevLPs,
    rageQuitLSDs
  ])

  const refetch = () => setIsRefetch(!isRefetch)

  return {
    balance,
    loading: loading || validatorLoading,
    isEligible,
    lastInteractedTimestamp,
    refetch,
    checkLPTokenIfEligible,
    checkGiantLPTokenIfEligible,
    LSDs:
      mode === WITHDRAW_MODE.STAKING
        ? protectedLSDs
        : mode === WITHDRAW_MODE.FEES_MEV
        ? feesAndMevLSDs
        : mode === WITHDRAW_MODE.UNSTAKED_VALIDATOR
        ? rageQuitLSDs
        : null,
    blsKeys:
      mode === WITHDRAW_MODE.STAKING
        ? protectedBlsKeys
        : mode === WITHDRAW_MODE.FEES_MEV
        ? feesAndMevBlsKeys
        : mode === WITHDRAW_MODE.UNSTAKED_VALIDATOR
        ? rageQuitValidators
        : null
  }
}
